import { ApolloError } from '@apollo/client'
import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import {
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  defer,
  delay,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  ignoreElements,
  map,
  merge,
  mergeWith,
  Observable,
  of,
  skipWhile,
  Subject,
  switchMap,
  take,
  takeWhile,
} from 'rxjs'
import { assign, createMachine, interpret } from 'xstate'

import {
  ApplyCouponToCartDocument,
  CartFragment,
  RemoveCouponFromCartDocument,
} from '../../../graphql/magento'
import { CustomError, logAndCaptureException } from '../../../utils/errorTools'
import { left, right } from '../../../utils/result'
import { coerceCaughtToLeftWith } from '../../../utils/rx/errors'
import { retryWithBackoff } from '../../../utils/rx/operators'
import { remoteEScriptCartUpdateStatus$ } from '../../cross-tab/dispensary'
import { dispensaryOptedIntoGlobalPromotions$ } from '../../dispensaries/hooks/useDispensaryOptedIntoGlobalPromotions'
import { latestEScriptCartUpdateStatus } from '../../dispensaries/state'
import { UseCartGet } from '../hooks/useCartGet'
import { cartSettled$ } from './cart-mutation'
import { enqueueCartWrite } from './cart-write-queue'

const [globalPromotionsInput$, setGlobalPromotions] = createSignal<string[]>()

export { setGlobalPromotions }

const retryApplyGlobalPromotionsTick$ = new Subject<void>()

export const retryApplyGlobalPromotions = () => retryApplyGlobalPromotionsTick$.next()

class ApplyGlobalPromotionError extends CustomError {
  override name = 'ApplyGlobalPromotionError'
}

/**
 * Magento returned an error specific to an attempted coupon code. E.g.:
 * - "Coupon code has exceeded the maximum number of uses."
 * - "The coupon code isn't valid. Verify the code and try again."
 *
 * Ref: https://bitbucket.org/acadaca/dfh-magento2/src/97e32df01273434b8a55d792e366925d875d5fbe/webroot/app/code/DesignsForHealth/SalesRule/Plugin/Model/CouponManagement.php?at=master
 */
class CouponCodeNotValidError extends CustomError {
  override name = 'CouponCodeNotValidError'
}

/**
 * Magento returned a fallback error. E.g.:
 * - "Coupon code cannot be applied."
 *
 * Ref: https://bitbucket.org/acadaca/dfh-magento2/src/97e32df01273434b8a55d792e366925d875d5fbe/webroot/app/code/DesignsForHealth/SalesRule/Plugin/Model/CouponManagement.php?at=master
 */
class GenericCouponCodeNotAppliedError extends CustomError {
  override name = 'GenericCouponCodeNotAppliedError'
}

/**
 * Magento returned an error that is not specific to the coupon code or interaction with
 * already applied promos. E.g.:
 * - "Could not apply more coupon codes on this cart."
 * - "Coupon quantity limit has been reached."
 *
 * Ref: https://bitbucket.org/acadaca/dfh-magento2/src/97e32df01273434b8a55d792e366925d875d5fbe/webroot/app/code/DesignsForHealth/SalesRule/Plugin/Model/CouponManagement.php?at=master
 */
class NonSpecificApplyCouponError extends CustomError {
  override name = 'NonSpecificApplyCouponError'
}

/**
 * Magento returned one of these errors:
 * - "Current promos result in a higher discount."
 * - "You are using an unique coupon. To add other coupons, please remove this unique one."
 *
 * Ref: https://bitbucket.org/acadaca/dfh-magento2/src/97e32df01273434b8a55d792e366925d875d5fbe/webroot/app/code/DesignsForHealth/SalesRule/Plugin/Model/CouponManagement.php?at=master
 */
class NotCompatibleWithAppliedPromosError extends CustomError {
  override name = 'NotCompatibleWithAppliedPromosError'
}

class RemoveGlobalPromotionError extends CustomError {
  override name = 'RemoveGlobalPromotionError'
}

type MinimumViableCart = CartFragment & {
  id: NonNullable<CartFragment['id']>
  items: NonNullable<CartFragment['items']>
}

type CartLoadedAndViable = UseCartGet & {
  loading: false
  cart: MinimumViableCart
}

function isCartLoadedAndViable(state: UseCartGet): state is CartLoadedAndViable {
  const { cart, loading } = state
  return !loading && cart != null && !!cart.id && !!cart.items?.length
}

interface AppliedCouponsBrand {
  readonly AppliedCoupons: unique symbol
}

// newtype
type AppliedCoupons = string & AppliedCouponsBrand

const mkAppliedCoupons = (codes: string[]) => codes.slice().sort().join(';') as AppliedCoupons

interface IncompatiblePromotions {
  appliedCoupons: AppliedCoupons
  globalPromotion: string
  timestamp: number
}

interface InvalidPromotion {
  globalPromotion: string
  timestamp: number
}

type MachineContext = CartLoadedAndViable & {
  globalPromotions: string[]
  optedIn: boolean
  /**
   * Other applied coupons/promotions which have resulted in a `NotCompatibleWithAppliedPromosError`
   * when trying to apply global promotions. Track to avoid repeating the mutation expecting a
   * different outcome.
   */
  incompatiblePromotions: IncompatiblePromotions[]
  invalidPromotions: InvalidPromotion[]
}

const machine = (initialContext: MachineContext) =>
  createMachine(
    {
      schema: {
        context: {} as MachineContext,
        events: {} as
          | { type: 'OPTED_IN' }
          | { type: 'NOT_OPTED_IN' }
          | { type: 'CART_CHANGED'; value: CartLoadedAndViable }
          | { type: 'CANCEL' }
          | { type: 'RETRY_APPLY_REQUESTED' }
          | { type: 'INCOMPATIBLE_PROMOTIONS'; value: IncompatiblePromotions }
          | { type: 'INVALID_PROMOTION'; value: InvalidPromotion },
      },
      context: initialContext,
      id: 'globalPromotions',
      initial: 'resolveDispensarySettings',
      states: {
        resolveDispensarySettings: {
          invoke: {
            src: (_context, _event) =>
              dispensaryOptedIntoGlobalPromotions$.pipe(
                map((optedIntoGlobalPromotions) => ({
                  type: optedIntoGlobalPromotions ? 'OPTED_IN' : 'NOT_OPTED_IN',
                })),
              ),
          },
          on: {
            OPTED_IN: {
              actions: [assign({ optedIn: true })],
              target: 'applyIfNeeded',
            },
            NOT_OPTED_IN: {
              // should already be `false`, but let's assign for clarity
              actions: [assign({ optedIn: false })],
              target: 'removeIfPresent',
            },
          },
        },
        applyIfNeeded: {
          invoke: {
            src: (context, _event) => tryApplyGlobalPromotions$(context, Date.now()),
            onDone: 'idle',
          },
          on: {
            CANCEL: 'idle',
            INCOMPATIBLE_PROMOTIONS: { actions: 'assignToContext' },
            INVALID_PROMOTION: { actions: 'assignToContext' },
          },
        },
        removeIfPresent: {
          invoke: {
            src: (context, _event) => removeGlobalPromotionsIfPresent$(context),
            onDone: 'idle',
          },
          on: {
            CANCEL: 'idle',
          },
        },
        idle: {
          // watch cart state for changes to cart, then update context and transition to
          // `applyIfNeeded` or `removeIfPresent` depending on `optedIn` value
          invoke: {
            src: (context, _event) =>
              merge(
                // monitor cart state (as long as eScript cart update not in progress)
                combineLatest([latestEScriptCartUpdateStatus, remoteEScriptCartUpdateStatus$]).pipe(
                  map(([local, remote]) =>
                    local === 'in_progress' || remote === 'in_progress' ? 'in_progress' : 'idle',
                  ),
                  distinctUntilChanged(),
                  switchMap((status) => (status !== 'idle' ? EMPTY : cartSettled$)),
                  skipWhile(({ cart }) => !cart || cart === context.cart),
                  filter(isCartLoadedAndViable),
                  // try to avoid overlapping with user modifications to cart
                  debounceTime(500),
                  map((cartState) => ({
                    type: 'CART_CHANGED',
                    value: cartState,
                  })),
                ),
                // monitor retry requests (e.g. user navigates to checkout review step)
                retryApplyGlobalPromotionsTick$.pipe(
                  map(() => ({
                    type: 'RETRY_APPLY_REQUESTED',
                  })),
                ),
                // monitor opted-in state (e.g. user redirected from global to dispensary context)
                dispensaryOptedIntoGlobalPromotions$.pipe(
                  switchMap((optedIntoGlobalPromotions) =>
                    optedIntoGlobalPromotions === context.optedIn
                      ? EMPTY
                      : of({
                          type: optedIntoGlobalPromotions ? 'OPTED_IN' : 'NOT_OPTED_IN',
                        }),
                  ),
                ),
              ),
          },
          on: {
            CART_CHANGED: [
              {
                actions: 'assignToContext',
                // remain idle if all global promotions are incompatible with applied coupons
                cond: 'isOptedInButAllPromosIncompatible',
              },
              {
                actions: 'assignToContext',
                // opted in (and incompatible promo(s) not present) - try to apply global promotion(s)
                cond: 'isOptedIn',
                target: 'applyIfNeeded',
              },
              {
                actions: 'assignToContext',
                target: 'removeIfPresent',
              },
            ],
            // retry requested (e.g. user navigated to review step of checkout)
            RETRY_APPLY_REQUESTED: {
              actions: 'clearIncompatibleAndInvalid',
              cond: 'isOptedIn',
              target: 'applyIfNeeded',
            },
            // opt-in status changed to `true`
            OPTED_IN: {
              actions: [assign({ optedIn: true })],
              target: 'applyIfNeeded',
            },
            // opt-in status changed to `false`
            NOT_OPTED_IN: {
              actions: [assign({ optedIn: false })],
              target: 'removeIfPresent',
            },
          },
        },
      },
    },
    {
      actions: {
        assignToContext: assign((context, event) =>
          event.type === 'CART_CHANGED'
            ? { ...context, ...event.value }
            : event.type === 'INCOMPATIBLE_PROMOTIONS'
              ? {
                  ...context,
                  // save incompatible promotions pairing to context to avoid unnecessary requests
                  // in the future
                  incompatiblePromotions: [
                    ...expireIncompatiblePromotions(context.incompatiblePromotions, Date.now()),
                    event.value,
                  ],
                }
              : event.type === 'INVALID_PROMOTION'
                ? {
                    ...context,
                    // save invalid promotions to avoid unnecessary requests in the future
                    invalidPromotions: [
                      ...expireInvalidPromotions(context.invalidPromotions, Date.now()),
                      event.value,
                    ],
                  }
                : context,
        ),
        clearIncompatibleAndInvalid: assign((context, event) =>
          event.type === 'RETRY_APPLY_REQUESTED'
            ? {
                ...context,
                // clear incompatible and invalid promotions so we can retry all global promotions
                incompatiblePromotions: [],
                invalidPromotions: [],
              }
            : context,
        ),
      },
      guards: {
        isOptedIn: (context, _event) => context.optedIn,
        // dispensary has opted into global promotions but all of the available promotions are
        // incompatible with currently applied coupons (optimization to avoid work)
        isOptedInButAllPromosIncompatible: (context, event) => {
          if (event.type !== 'CART_CHANGED' || !context.optedIn) {
            return false
          }
          const appliedCoupons = mkAppliedCoupons(
            event.value.cart.applied_coupons?.reduce((acc, appliedCoupon) => {
              if (appliedCoupon) {
                acc.push(appliedCoupon.code)
              }
              return acc
            }, [] as string[]) ?? [],
          )
          return context.globalPromotions.every((globalPromotion) =>
            context.incompatiblePromotions.some(
              (incompatiblePromo) =>
                incompatiblePromo.globalPromotion === globalPromotion &&
                incompatiblePromo.appliedCoupons === appliedCoupons,
            ),
          )
        },
      },
    },
  )

function isCartNotFoundOrActiveError(err: ApolloError): boolean {
  const message = err.message
  return (
    message.includes('Could not find a cart with ID') || message.includes("The cart isn't active")
  )
}

function parseApplyCouponError(
  err: ApolloError,
):
  | ApplyGlobalPromotionError
  | CouponCodeNotValidError
  | GenericCouponCodeNotAppliedError
  | NonSpecificApplyCouponError
  | NotCompatibleWithAppliedPromosError {
  const message = err.message
  if (
    message.includes('Coupon code has exceeded the maximum number of uses') ||
    message.includes("The coupon code isn't valid")
  ) {
    return new CouponCodeNotValidError(message, err)
  }
  if (
    message.includes('Current promos result in a higher discount') ||
    message.includes('You are using an unique coupon')
  ) {
    return new NotCompatibleWithAppliedPromosError(message, err)
  }
  if (
    message.includes('Could not apply more coupon codes on this cart') ||
    message.includes('Coupon quantity limit has been reached')
  ) {
    return new NonSpecificApplyCouponError(message, err)
  }
  if (message.includes('Coupon code cannot be applied')) {
    return new GenericCouponCodeNotAppliedError(message, err)
  }
  return new ApplyGlobalPromotionError(err.message, err)
}

const applyCouponToCart = (
  { cartId, couponCode }: { cartId: string; couponCode: string },
  refetch: () => void,
): Observable<
  | {
      _tag: 'Left'
      error:
        | ApplyGlobalPromotionError
        | CouponCodeNotValidError
        | GenericCouponCodeNotAppliedError
        | NonSpecificApplyCouponError
        | NotCompatibleWithAppliedPromosError
    }
  | { _tag: 'Right'; data: undefined }
> =>
  enqueueCartWrite('global-promotions', ({ client, cart, token }) =>
    // decided against another cart (e.g. before a merge): the machine re-evaluates the new one
    cart.id !== cartId
      ? of(right(undefined))
      : defer(() =>
          client.mutate({
            mutation: ApplyCouponToCartDocument,
            variables: { cartId: cart.id, couponCode },
            ...(token && { context: { token } }),
            errorPolicy: 'all',
          }),
        ).pipe(
          map(({ errors }) => {
            const firstError = errors?.[0]
            const error = firstError ? new ApolloError({ graphQLErrors: [firstError] }) : undefined
            if (!error) {
              // discard response
              return right(undefined)
            }
            if (isCartNotFoundOrActiveError(error)) {
              // trigger refetch if M2 returns cart not found error
              refetch()
              return left(new ApplyGlobalPromotionError(error.message, error))
            }
            const applyError = parseApplyCouponError(error)
            // avoid capturing errors that don't indicate a bug or problem
            if (applyError instanceof ApplyGlobalPromotionError) {
              logAndCaptureException(applyError)
            }
            return left(applyError)
          }),
        ),
  ).pipe(
    // retry network errors one enqueued attempt at a time, so the queue and the checkout writes
    // behind it are free during the backoff
    retryWithBackoff({ maxDelayMs: 60000 }),
    catchError(
      coerceCaughtToLeftWith((error) => new ApplyGlobalPromotionError(error.message, error)),
    ),
  )

const removeCouponFromCart = (
  { cartId, couponCode }: { cartId: string; couponCode: string },
  refetch: () => void,
): Observable<
  { _tag: 'Left'; error: RemoveGlobalPromotionError } | { _tag: 'Right'; data: undefined }
> =>
  enqueueCartWrite('global-promotions', ({ client, cart, token }) =>
    // decided against another cart (e.g. before a merge): the machine re-evaluates the new one
    cart.id !== cartId
      ? of(right(undefined))
      : defer(() =>
          client.mutate({
            mutation: RemoveCouponFromCartDocument,
            variables: { cartId: cart.id, couponCode },
            ...(token && { context: { token } }),
            errorPolicy: 'all',
          }),
        ).pipe(
          map(({ errors }) => {
            const firstError = errors?.[0]
            const error = firstError ? new ApolloError({ graphQLErrors: [firstError] }) : undefined
            if (!error) {
              // discard response
              return right(undefined)
            }
            if (isCartNotFoundOrActiveError(error)) {
              // trigger refetch if M2 returns cart not found error
              refetch()
              return left(new ApplyGlobalPromotionError(error.message, error))
            }
            const removeError = new RemoveGlobalPromotionError(error.message, error)
            logAndCaptureException(removeError)
            return left(removeError)
          }),
        ),
  ).pipe(
    // retry network errors one enqueued attempt at a time, so the queue and the checkout writes
    // behind it are free during the backoff
    retryWithBackoff({ maxDelayMs: 60000 }),
    catchError(
      coerceCaughtToLeftWith((error) => new RemoveGlobalPromotionError(error.message, error)),
    ),
  )

const expireIncompatiblePromotions = (
  incompatiblePromotions: ReadonlyArray<IncompatiblePromotions>,
  currentTimestamp: number,
): IncompatiblePromotions[] => {
  const discardBefore = currentTimestamp - 30 * 60 * 1000 // 30 minutes
  return incompatiblePromotions.filter(({ timestamp }) => timestamp >= discardBefore)
}

const expireInvalidPromotions = (
  invalidPromotions: ReadonlyArray<InvalidPromotion>,
  currentTimestamp: number,
): InvalidPromotion[] => {
  const discardBefore = currentTimestamp - 30 * 60 * 1000 // 30 minutes
  return invalidPromotions.filter(({ timestamp }) => timestamp >= discardBefore)
}

const tryApplyGlobalPromotions$ = (
  context: MachineContext,
  currentTimestamp: number,
): Observable<
  | { type: 'INCOMPATIBLE_PROMOTIONS'; value: IncompatiblePromotions }
  | { type: 'INVALID_PROMOTION'; value: InvalidPromotion }
> => {
  const { cart, globalPromotions, refetch } = context
  const cartId = cart.id
  // cart must contain at least one item to call `applyCouponToCart` mutation
  if (!cartId || !cart.items?.length) {
    return EMPTY
  }
  const appliedGlobalPromotions = cart?.applied_coupons?.filter(
    (coupon): coupon is NonNullable<typeof coupon> & { code: string } =>
      !!coupon?.code && globalPromotions.some((promotion) => coupon.code === promotion),
  )
  // if at least one global promotion is applied, no further action is required
  // TODO: update logic if business requires reevaluating coupon application
  if (appliedGlobalPromotions?.length) {
    return EMPTY
  }
  // collect all of the global promotions we should try to apply, filtering out incompatible
  // promotions
  const appliedCoupons = mkAppliedCoupons(
    cart.applied_coupons?.reduce((acc, appliedCoupon) => {
      if (appliedCoupon) {
        acc.push(appliedCoupon.code)
      }
      return acc
    }, [] as string[]) ?? [],
  )
  // avoid retrying incompatible or invalid promotions (until the entries expire)
  const incompatiblePromotions = expireIncompatiblePromotions(
    context.incompatiblePromotions,
    currentTimestamp,
  )
  const invalidPromotions = expireInvalidPromotions(context.invalidPromotions, currentTimestamp)
  const globalPromotionsToTry = globalPromotions.filter(
    (globalPromotion) =>
      !incompatiblePromotions.some(
        (incompatiblePromo) =>
          incompatiblePromo.globalPromotion === globalPromotion &&
          incompatiblePromo.appliedCoupons === appliedCoupons,
      ) &&
      !invalidPromotions.some(
        (invalidPromotion) => invalidPromotion.globalPromotion === globalPromotion,
      ),
  )
  return !globalPromotionsToTry.length
    ? // exit early if there are no global promotions to try
      EMPTY
    : // try applying at least one global promotion
      from(globalPromotionsToTry).pipe(
        concatMap((couponCode) => {
          // console.debug(`Applying global promotion to cart ${cartId}:`, couponCode)
          return applyCouponToCart({ cartId, couponCode }, refetch).pipe(
            switchMap(
              (
                result,
              ): Observable<
                | { type: 'INCOMPATIBLE_PROMOTIONS'; value: IncompatiblePromotions }
                | { type: 'INVALID_PROMOTION'; value: InvalidPromotion }
              > => {
                if (result._tag === 'Right') {
                  return EMPTY
                }
                if (result.error instanceof CouponCodeNotValidError) {
                  return of({
                    type: 'INVALID_PROMOTION',
                    value: { globalPromotion: couponCode, timestamp: Date.now() },
                  })
                }
                // NOTE: ensure `appliedCoupons` is not empty as M2 returns "higher discount" error
                // when promo rules prevent applying to any item in cart
                if (result.error instanceof NotCompatibleWithAppliedPromosError && appliedCoupons) {
                  return of({
                    type: 'INCOMPATIBLE_PROMOTIONS',
                    value: { appliedCoupons, globalPromotion: couponCode, timestamp: Date.now() },
                  })
                }
                return EMPTY
              },
            ),
          )
        }),
      )
}

const removeGlobalPromotionsIfPresent$ = ({
  cart,
  globalPromotions,
  refetch,
}: {
  cart: MinimumViableCart
  globalPromotions: string[]
  refetch: UseCartGet['refetch']
}): Observable<never> => {
  const cartId = cart.id
  // cart must contain at least one item to call `removeCouponFromCart` mutation
  if (!cartId || !cart.items?.length) {
    return EMPTY
  }
  const appliedGlobalPromotions = cart?.applied_coupons?.filter(
    (coupon): coupon is NonNullable<typeof coupon> & { code: string } =>
      !!coupon?.code && globalPromotions.some((promotion) => coupon.code === promotion),
  )
  return !appliedGlobalPromotions?.length
    ? // global promotions not found in the applied coupons - nothing to do
      EMPTY
    : from(appliedGlobalPromotions).pipe(
        concatMap(({ code: couponCode }) => {
          // console.debug(`Removing global promotion from cart ${cartId}:`, coupon.code)
          return removeCouponFromCart({ cartId, couponCode }, refetch)
        }),
        takeWhile((result) => result._tag !== 'Left'),
        ignoreElements(),
      )
}

export const [useGlobalPromotionsMachine, globalPromotionsMachine$] = bind(
  combineLatest([
    cartSettled$.pipe(
      // wait for cart to settle (loaded, no merge running) and have at least one item
      filter(isCartLoadedAndViable),
    ),
    globalPromotionsInput$,
  ]).pipe(
    take(1),
    // delay start briefly to minimize initial application load tax
    delay(1000),
    switchMap(([cartState, globalPromotions]) =>
      !globalPromotions.length
        ? EMPTY
        : interpret(
            machine({
              ...cartState,
              globalPromotions,
              // will resolve during initial state
              optedIn: false,
              // populated over time
              incompatiblePromotions: [],
              // populated over time
              invalidPromotions: [],
            }),
          ).start(),
    ),
    // tap((state) => {
    //   console.debug(`globalPromotions state:`, state)
    // }),
    catchError((err) => {
      const error = new Error(
        `Failed to interpret global promotions machine: ${err}`,
        err instanceof Error ? err : undefined,
      )
      logAndCaptureException(error)
      return of(null)
    }),
    // hold subscription so we can check the eScript cart update status in state machine
    mergeWith(
      latestEScriptCartUpdateStatus.pipe(ignoreElements()),
      retryApplyGlobalPromotionsTick$.pipe(ignoreElements()),
    ),
  ),
  null,
)
